import { useId } from '@chakra-ui/react';
import { useFormik } from 'formik';
import { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { boolean, number, object, string } from 'yup';

import { COUNTRIES, COUNTRY_REGIONS } from '@frontend/addresses';
import {
  Button,
  Checkbox,
  CheckboxGroup,
  Divider,
  DrawerBody,
  DrawerButtonGroup,
  DrawerFooter,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Input,
  InputGroup,
  InputLeftAddon,
  InputRightAddon,
  PhoneInput,
  PhoneInputCountrySelect,
  PhoneInputField,
  Radio,
  RadioGroup,
  Textarea,
} from '@frontend/components';

import { ACCOUNT_TYPES } from 'constants/all';

import {
  CountrySelect,
  RegionSelect,
} from 'modules/common/components/addressFormFields';
import {
  TeamMemberSelect,
  type TeamMemberOptionType,
} from 'modules/common/smartComponents/TeamMemberSelect';
import { usePaymentTermsQuery } from 'modules/contacts/searchPage/data/queries';

import {
  getDefaultCreditCardsEnabledForAccounts,
  getDefaultFacilityCountry,
  getDefaultPaymentTermsForAccounts,
  getDefaultPurchaseOrdersEnabledForAccounts,
  getSupplierTaxRate,
  hasCreditCardCheckoutEnabled,
} from 'store/session/selectors';

import { FormikHooksReactSelectWrapper } from 'utils/FormikHooksReactSelectWrapper';
import { validateOptionalUrlWithOptionalProtocol } from 'utils/validationFunctions';

import { PaymentTermsSelect } from './PaymentTermsSelect';
import { save as saveAction, setPaymentTerms } from './data/actions';
import { MODES } from './data/constants';
import { getIsSaving } from './data/reducer';

type AccountFormData = {
  address1: string;
  address2: string;
  city: string;
  country: string;
  creditCardsEnabled: boolean;
  creditLine: number | null;
  erpCode: string;
  name: string;
  notes: string;
  paymentTermsId: string | undefined;
  purchaseOrders: boolean;
  phone: string | null;
  phoneExt: string;
  postalCode: string;
  salespersonId: number | null;
  state: string;
  taxExempt: boolean;
  taxRate: number | null;
  type: string;
  url: string;
};

interface AccountFormProps {
  account?: any;
  mode: (typeof MODES)[keyof typeof MODES];
  onClose: () => void;
  onSaveCallback: (args: { account: any }) => void;
  prefilledAccountName?: string;
}

export const AccountForm = ({
  account,
  mode,
  onClose,
  onSaveCallback,
  prefilledAccountName,
}: AccountFormProps) => {
  const isSaving = useSelector(getIsSaving);

  const dispatch = useDispatch();

  const save = useCallback(
    (
      account: any,
      accountId: number | null,
      mode: (typeof MODES)[keyof typeof MODES],
      onClose: () => void,
      onSaveCallback: (args: { account: any }) => void,
    ) => {
      dispatch(
        saveAction.request({
          ...account,
          accountId,
          mode,
          onClose,
          onSaveCallback,
        }),
      );
    },
    [dispatch],
  );

  const supplierDefaultTaxRate = useSelector(getSupplierTaxRate);
  const hasDefaultCreditCardsEnabled = useSelector(
    getDefaultCreditCardsEnabledForAccounts,
  );
  const hasDefaultPurchaseOrdersEnabled = useSelector(
    getDefaultPurchaseOrdersEnabledForAccounts,
  );

  const isCreditCardCheckoutEnabled = useSelector(hasCreditCardCheckoutEnabled);

  const defaultPaymentTerms = useSelector(getDefaultPaymentTermsForAccounts);
  const editModeDefaultPaymentTerms = account?.paymentTerms?.id;
  const createModeDefaultPaymentTerms = defaultPaymentTerms?.id;

  const defaultCountry = useSelector(getDefaultFacilityCountry);

  const { data: paymentTerms } = usePaymentTermsQuery();

  useEffect(() => {
    if (paymentTerms) {
      dispatch(setPaymentTerms(paymentTerms));
    }
  }, [paymentTerms, dispatch]);

  const formId = useId(undefined, 'account-create-form');
  const {
    errors,
    values,
    touched,
    handleBlur,
    handleChange,
    handleSubmit,
    setFieldTouched,
    setFieldValue,
  } = useFormik<AccountFormData>({
    initialValues: {
      address1: account?.soldToAddress?.address1 ?? '',
      address2: account?.soldToAddress?.address2 ?? '',
      city: account?.soldToAddress?.city ?? '',
      country: account?.soldToAddress?.country?.abbr ?? defaultCountry.abbr,
      creditCardsEnabled:
        account?.creditCardsEnabled ?? hasDefaultCreditCardsEnabled,
      creditLine: account?.creditLine ?? null,
      erpCode: account?.erpCode ?? '',
      name: account?.name ?? prefilledAccountName ?? '',
      notes: account?.notes ?? '',
      paymentTermsId:
        mode == MODES.EDIT
          ? editModeDefaultPaymentTerms
          : createModeDefaultPaymentTerms,
      purchaseOrders:
        account?.purchaseOrders ?? hasDefaultPurchaseOrdersEnabled,
      phone: account?.phone ?? '',
      phoneExt: account?.phoneExt ?? '',
      postalCode: account?.soldToAddress?.postalCode ?? '',
      salespersonId: account?.salesperson?.id ?? null,
      state: account?.soldToAddress?.state?.abbr ?? '',
      taxExempt: account?.taxExempt ?? true,
      taxRate: account?.taxRate ?? null,
      type: account?.type ?? ACCOUNT_TYPES.CUSTOMER,
      url: account?.url ?? '',
    },
    onSubmit: ({
      address1,
      address2,
      city,
      country,
      creditCardsEnabled,
      creditLine,
      erpCode,
      name,
      notes,
      paymentTermsId,
      phone,
      phoneExt,
      postalCode,
      purchaseOrders,
      salespersonId,
      state,
      taxExempt,
      taxRate,
      type,
      url,
    }) => {
      save(
        {
          address1,
          address2,
          city,
          country,
          creditCardsEnabled,
          creditLine,
          erpCode: erpCode || null,
          name,
          notes: notes || null,
          paymentTermsId,
          phone,
          phoneExt,
          postalCode,
          purchaseOrders,
          salespersonId,
          state,
          taxExempt,
          taxRate,
          type,
          url: url || null,
        },
        account?.id ?? null,
        mode,
        onClose,
        onSaveCallback,
      );
    },
    validationSchema: object().shape({
      address1: string().test(
        'address-covalidation',
        'This field is required',
        (value, context) =>
          !!value ||
          !(
            context.parent.address2 ||
            context.parent.city ||
            context.parent.postalCode ||
            context.parent.state
          ),
      ),
      address2: string().nullable(),
      city: string().test(
        'address-covalidation',
        'This field is required',
        (value, context) =>
          !!value ||
          !(
            context.parent.address1 ||
            context.parent.address2 ||
            context.parent.postalCode ||
            context.parent.state
          ),
      ),
      country: string()
        .oneOf(
          COUNTRIES.map((country) => country.abbreviation),
          'Please enter a valid country',
        )
        .nullable(),
      creditCardsEnabled: boolean(),
      creditLine: number()
        .nullable()
        .positive('Credit line must be a positive number'),
      erpCode: string().nullable(),
      name: string().required('This field is required'),
      notes: string().nullable(),
      paymentTermsId: number().required('This field is required'),
      phone: string().nullable(),
      phoneExt: string().nullable(),
      purchaseOrders: boolean(),
      postalCode: string().nullable(),
      salespersonId: number().nullable(),
      state: string()
        .nullable()
        .test('country-covalidation', 'Invalid value', (value, context) => {
          if (value) {
            const selectedCountry = context.parent.country;
            const countryRegionData =
              COUNTRY_REGIONS[selectedCountry as string];
            if (countryRegionData) {
              return countryRegionData.regions.some(
                (region) => value === region.abbreviation,
              );
            }
          }
          return true;
        })
        .test(
          'address-covalidation',
          'This field is required',
          (value, context) => {
            const selectedCountry = context.parent.country;
            const availableRegions =
              COUNTRY_REGIONS[selectedCountry as string]?.regions ?? [];

            return (
              !!value ||
              !availableRegions.length ||
              !(
                context.parent.address1 ||
                context.parent.address2 ||
                context.parent.postalCode ||
                context.parent.city
              )
            );
          },
        ),
      taxExempt: boolean(),
      taxRate: number()
        .nullable()
        .min(0, 'Tax rate must be a positive number')
        .max(100, 'Tax rate cannot be more than 100%'),
      type: string()
        .oneOf(Object.values(ACCOUNT_TYPES), 'Invalid account type')
        .required('This field is required'),
      url: string().test({
        name: 'validate-optional-url-with-optional-protocol',
        test: validateOptionalUrlWithOptionalProtocol,
        message: 'Invalid URL',
      }),
    }),
    validateOnChange: false,
    validateOnBlur: true,
  });

  return (
    <>
      <DrawerBody
        as="form"
        display="flex"
        flexDir="column"
        gap={2}
        onSubmit={handleSubmit}
        autoComplete="off"
        id={formId}
      >
        <FormControl
          isRequired
          isInvalid={!!errors.name && touched.name}
          id="name"
        >
          <FormLabel>Account Name</FormLabel>
          <Input
            autoComplete="new-password"
            name="name"
            onChange={handleChange}
            onBlur={handleBlur}
            value={values.name}
          />
          <FormErrorMessage>{errors.name}</FormErrorMessage>
        </FormControl>
        <Flex alignItems="flex-end" gap={0.5}>
          <FormControl isInvalid={!!errors.phone && touched.phone} id="phone">
            <FormLabel>Phone</FormLabel>
            <PhoneInput
              defaultCountry={defaultCountry.alpha2}
              value={values.phone}
              onChange={(value) => setFieldValue('phone', value)}
            >
              <PhoneInputCountrySelect />
              <PhoneInputField
                name="phone"
                autoComplete="new-password"
                onBlur={handleBlur}
              />
            </PhoneInput>
            <FormErrorMessage>{errors.phone}</FormErrorMessage>
          </FormControl>
          <FormControl
            isInvalid={!!errors.phoneExt && touched.phoneExt}
            w={20}
            id="phoneExt"
          >
            <InputGroup>
              <InputLeftAddon>Ext.</InputLeftAddon>
              <Input
                autoComplete="new-password"
                name="phoneExt"
                onChange={handleChange}
                onBlur={handleBlur}
                value={values.phoneExt}
              />
            </InputGroup>
            <FormErrorMessage>{errors.phoneExt}</FormErrorMessage>
          </FormControl>
        </Flex>
        <FormControl isInvalid={!!errors.url && touched.url} id="url">
          <FormLabel>Website</FormLabel>
          <Input
            autoComplete="new-password"
            name="url"
            onChange={handleChange}
            onBlur={handleBlur}
            value={values.url}
          />
          <FormErrorMessage>{errors.url}</FormErrorMessage>
        </FormControl>
        <FormControl isInvalid={!!errors.notes && touched.notes} id="notes">
          <FormLabel>Notes</FormLabel>
          <Textarea
            autoComplete="new-password"
            name="notes"
            onChange={handleChange}
            onBlur={handleBlur}
            value={values.notes}
          />
          <FormErrorMessage>{errors.notes}</FormErrorMessage>
        </FormControl>
        <FormControl
          isRequired
          isInvalid={!!errors.type && touched.type}
          id="type"
        >
          <FormLabel>Type</FormLabel>
          <RadioGroup name="type" value={values.type} direction="row">
            <Radio onChange={handleChange} value={ACCOUNT_TYPES.CUSTOMER}>
              Customer
            </Radio>
            <Radio onChange={handleChange} value={ACCOUNT_TYPES.VENDOR}>
              Vendor
            </Radio>
          </RadioGroup>
          <FormErrorMessage>{errors.type}</FormErrorMessage>
        </FormControl>
        <FormControl
          isInvalid={!!errors.salespersonId && touched.salespersonId}
          id="salespersonId"
        >
          <FormLabel>Salesperson</FormLabel>
          <TeamMemberSelect
            isClearable={false}
            mode="assignment"
            name="salespersonId"
            onChange={(option: TeamMemberOptionType | null) => {
              setFieldValue('salespersonId', option?.value ?? null);
            }}
            value={values.salespersonId}
          />
          <FormErrorMessage>{errors.salespersonId}</FormErrorMessage>
        </FormControl>
        <Divider />
        <FormControl
          isInvalid={!!errors.address1 && touched.address1}
          id="address1"
        >
          <FormLabel>Address 1</FormLabel>
          <Input
            autoComplete="new-password"
            name="address1"
            onChange={handleChange}
            onBlur={handleBlur}
            value={values.address1}
          />
          <FormErrorMessage>{errors.address1}</FormErrorMessage>
        </FormControl>
        <FormControl
          isInvalid={!!errors.address2 && touched.address2}
          id="address2"
        >
          <FormLabel>Address 2</FormLabel>
          <Input
            autoComplete="new-password"
            name="address2"
            onChange={handleChange}
            onBlur={handleBlur}
            value={values.address2}
          />
          <FormErrorMessage>{errors.address2}</FormErrorMessage>
        </FormControl>
        <Flex gap={1.5}>
          <FormControl isInvalid={!!errors.city && touched.city} id="city">
            <FormLabel>City</FormLabel>
            <Input
              autoComplete="new-password"
              name="city"
              onChange={handleChange}
              onBlur={handleBlur}
              value={values.city}
            />
            <FormErrorMessage>{errors.city}</FormErrorMessage>
          </FormControl>
          <FormControl isInvalid={!!errors.state && touched.state} id="state">
            <FormLabel>
              {COUNTRY_REGIONS[values.country as string]?.regionLabel ??
                'State/Province'}
            </FormLabel>
            <FormikHooksReactSelectWrapper
              name="state"
              setFieldTouched={setFieldTouched}
              setFieldValue={setFieldValue}
              values={values}
            >
              <RegionSelect country={values.country} />
            </FormikHooksReactSelectWrapper>
            <FormErrorMessage>{errors.state}</FormErrorMessage>
          </FormControl>
        </Flex>
        <Flex gap={1.5}>
          <FormControl
            isInvalid={!!errors.postalCode && touched.postalCode}
            id="postalCode"
          >
            <FormLabel>
              {COUNTRY_REGIONS[values.country as string]?.postalCodeLabel ??
                'Postal Code'}
            </FormLabel>
            <Input
              autoComplete="new-password"
              name="postalCode"
              onChange={handleChange}
              onBlur={handleBlur}
              value={values.postalCode}
            />
            <FormErrorMessage>{errors.postalCode}</FormErrorMessage>
          </FormControl>
          <FormControl
            isInvalid={!!errors.country && touched.country}
            id="country"
          >
            <FormLabel>Country</FormLabel>
            <FormikHooksReactSelectWrapper
              name="country"
              setFieldTouched={setFieldTouched}
              setFieldValue={setFieldValue}
              values={values}
            >
              <CountrySelect
                onChange={() => {
                  setFieldValue('state', '');
                  setFieldValue('postalCode', '');
                }}
              />
            </FormikHooksReactSelectWrapper>
            <FormErrorMessage>{errors.country}</FormErrorMessage>
          </FormControl>
        </Flex>
        <Divider />
        <Flex gap={1.5}>
          <FormControl
            isInvalid={!!errors.erpCode && touched.erpCode}
            id="erpCode"
          >
            <FormLabel>ERP Code</FormLabel>
            <Input
              autoComplete="new-password"
              name="erpCode"
              onChange={handleChange}
              onBlur={handleBlur}
              value={values.erpCode}
            />
            <FormErrorMessage>{errors.erpCode}</FormErrorMessage>
          </FormControl>
          <FormControl
            isInvalid={!!errors.creditLine && touched.creditLine}
            id="creditLine"
          >
            <FormLabel>Credit Line</FormLabel>
            <InputGroup>
              <InputLeftAddon>$</InputLeftAddon>
              <Input
                autoComplete="new-password"
                name="creditLine"
                onChange={handleChange}
                onBlur={handleBlur}
                step="0.01"
                type="number"
                value={values.creditLine ?? ''}
              />
            </InputGroup>
            <FormErrorMessage>{errors.creditLine}</FormErrorMessage>
          </FormControl>
        </Flex>
        <Flex gap={1.5}>
          <FormControl
            isInvalid={!!errors.taxRate && touched.taxRate}
            id="taxRate"
          >
            <FormLabel>Tax Rate</FormLabel>
            <InputGroup>
              <Input
                autoComplete="new-password"
                disabled={values.taxExempt}
                name="taxRate"
                onChange={handleChange}
                onBlur={handleBlur}
                step="0.001"
                type="number"
                value={
                  values.taxExempt || !values.taxRate ? '' : values.taxRate
                }
              />
              <InputRightAddon>%</InputRightAddon>
            </InputGroup>
            <FormErrorMessage>{errors.taxRate}</FormErrorMessage>
          </FormControl>
          <FormControl mt={3.5} id="taxExempt">
            <Checkbox
              name="taxExempt"
              isChecked={values.taxExempt}
              onChange={(e) => {
                setFieldValue('taxExempt', e.target.checked);
                if (
                  !e.target.checked &&
                  !values.taxRate &&
                  supplierDefaultTaxRate
                ) {
                  setFieldValue('taxRate', supplierDefaultTaxRate);
                }
              }}
            >
              Tax Exempt
            </Checkbox>
          </FormControl>
        </Flex>
        <FormControl id="creditCardsEnabled">
          <FormLabel>Checkout Methods</FormLabel>
          <CheckboxGroup>
            <Checkbox
              name="creditCardsEnabled"
              onChange={handleChange}
              isDisabled={!isCreditCardCheckoutEnabled}
              isChecked={values.creditCardsEnabled}
            >
              Credit Card
            </Checkbox>
            <Checkbox
              name="purchaseOrders"
              onChange={handleChange}
              isChecked={values.purchaseOrders}
            >
              Purchase Order (PO)
            </Checkbox>
          </CheckboxGroup>
        </FormControl>
        <FormControl
          isRequired
          isInvalid={!!errors.paymentTermsId && touched.paymentTermsId}
          id="paymentTermsId"
        >
          <FormLabel>Payment Terms</FormLabel>
          <PaymentTermsSelect
            name="paymentTermsId"
            setValue={(value?: string) =>
              setFieldValue('paymentTermsId', value)
            }
            value={values.paymentTermsId}
          />
          <FormErrorMessage>{errors.paymentTermsId}</FormErrorMessage>
        </FormControl>
      </DrawerBody>
      <DrawerFooter>
        <DrawerButtonGroup>
          <Button variant="secondary" isLoading={isSaving} onClick={onClose}>
            Cancel
          </Button>
          <Button isLoading={isSaving} type="submit" form={formId}>
            {mode === MODES.EDIT ? 'Save Changes' : 'Create Account'}
          </Button>
        </DrawerButtonGroup>
      </DrawerFooter>
    </>
  );
};
